import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  AdminAPI, EvalClip, EvalRecording, EvalRun, EvalRunDetails, EvalScript,
  EvalSpeaker, Organization,
} from '../lib/api';
import { startEvalRecorder, EvalRecorder } from '../lib/recorder';
import {
  Loader2, Mic, Phone, Play, Plus, RefreshCw, Square, Trash2,
} from 'lucide-react';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'ta', label: 'Tamil' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ml', label: 'Malayalam' },
];
const CATEGORY_LABEL: Record<string, string> = {
  sentence: 'Sentence', short_phrase: 'Short phrase',
  one_word: 'One word', silence: 'Silence',
};
const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  dialing: 'bg-amber-100 text-amber-700',
  recording: 'bg-red-100 text-red-700',
  processing: 'bg-blue-100 text-blue-700',
  ready: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  running: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending', dialing: 'Dialling', recording: 'Recording',
  processing: 'Processing', ready: 'Ready', failed: 'Failed',
  running: 'Running', done: 'Complete',
};

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const reason = (e: unknown) => (e instanceof Error ? e.message : String(e));
const languageLabel = (code: string) => LANGUAGES.find(l => l.value === code)?.label || code;

/** An outcome line under a form: green when it worked, red when it didn't. */
type Note = { text: string; tone: 'ok' | 'error' } | null;
const NoteLine: React.FC<{ note: Note }> = ({ note }) =>
  note ? <p className={`text-xs ${note.tone === 'error' ? 'text-red-600' : 'text-emerald-600'}`}>{note.text}</p> : null;

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status] || 'bg-gray-100 text-gray-600'}`}>
    {STATUS_LABEL[status] || capitalise(status)}
  </span>
);

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;

/** "language=ta source=phone" → "Tamil · Phone only". */
function describeFilters(filters: Record<string, string> | null | undefined): string {
  const entries = Object.entries(filters || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined);
  if (!entries.length) return 'All clips';
  return entries.map(([k, v]) => {
    switch (k) {
      case 'language': return languageLabel(v);
      case 'source': return v === 'phone' ? 'Phone only' : v === 'browser' ? 'Browser only' : capitalise(v);
      case 'category': return CATEGORY_LABEL[v] || capitalise(v);
      default: return `${capitalise(k.replace(/_/g, ' '))}: ${v}`;
    }
  }).join(' · ');
}

const LoadingRow: React.FC<{ colSpan: number; what: string }> = ({ colSpan, what }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-8 text-center text-gray-400">
      <Loader2 className="inline w-4 h-4 animate-spin mr-2 align-text-bottom" />Loading {what}…
    </td>
  </tr>
);

export default function VoiceEvals() {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [tab, setTab] = useState<'recordings' | 'scripts' | 'speakers' | 'runs'>('recordings');

  const [speakers, setSpeakers] = useState<EvalSpeaker[]>([]);
  const [scripts, setScripts] = useState<EvalScript[]>([]);
  const [recordings, setRecordings] = useState<EvalRecording[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<{ text: string; detail: string } | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [sp, sc, re, ru, og] = await Promise.all([
        api.getEvalSpeakers(), api.getEvalScripts(), api.getEvalRecordings(),
        api.getEvalRuns(), api.getOrganizations(0, 1000),
      ]);
      setSpeakers(sp); setScripts(sc); setRecordings(re); setRuns(ru); setOrgs(og);
      setError(null);
    } catch (e) {
      setError({ text: "Couldn't load voice evals. Retry, or check the API.", detail: reason(e) });
    } finally {
      setLoaded(true);
    }
  }, [api]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Live statuses: poll while anything is mid-flight.
  const hasActive = recordings.some(r => ['dialing', 'recording', 'processing'].includes(r.status))
    || runs.some(r => r.status === 'running');
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(async () => {
      try {
        const [re, ru] = await Promise.all([api.getEvalRecordings(), api.getEvalRuns()]);
        setRecordings(re); setRuns(ru);
      } catch { /* transient */ }
    }, 4000);
    return () => clearInterval(t);
  }, [hasActive, api]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voice Evals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Speech-to-text (STT) benchmarking on our own audio — phone vs browser, across languages and reply lengths.
          </p>
        </div>
        <button onClick={loadAll} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3" title={error.detail}>
          <div className="flex items-center justify-between gap-3">
            <span>{error.text}</span>
            <button onClick={loadAll} className="shrink-0 px-3 py-1 rounded border border-red-300 bg-white text-xs hover:bg-red-100">
              Retry
            </button>
          </div>
          <details className="mt-1 text-xs text-red-600">
            <summary className="cursor-pointer">Details</summary>
            <p className="mt-1 break-words">{error.detail}</p>
          </details>
        </div>
      )}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {(['recordings', 'scripts', 'speakers', 'runs'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'recordings' && (
        <RecordingsTab api={api} speakers={speakers} scripts={scripts} orgs={orgs}
                       recordings={recordings} loaded={loaded} reload={loadAll} />
      )}
      {tab === 'scripts' && <ScriptsTab scripts={scripts} loaded={loaded} />}
      {tab === 'speakers' && <SpeakersTab api={api} speakers={speakers} loaded={loaded} reload={loadAll} />}
      {tab === 'runs' && <RunsTab api={api} runs={runs} loaded={loaded} reload={loadAll} />}
    </div>
  );
}

// ── Recordings ───────────────────────────────────────────────────────────────

const RecordingsTab: React.FC<{
  api: AdminAPI; speakers: EvalSpeaker[]; scripts: EvalScript[];
  orgs: Organization[]; recordings: EvalRecording[]; loaded: boolean; reload: () => Promise<void>;
}> = ({ api, speakers, scripts, orgs, recordings, loaded, reload }) => {
  const [speakerId, setSpeakerId] = useState('');
  const [scriptId, setScriptId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const [listNote, setListNote] = useState<string>('');
  const [openId, setOpenId] = useState<number | null>(null);

  // Browser recorder state
  const recorderRef = useRef<EvalRecorder | null>(null);
  const [recState, setRecState] = useState<'idle' | 'recording' | 'uploading'>('idle');
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (recState !== 'recording') return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [recState]);

  const script = scripts.find(s => String(s.id) === scriptId);

  const dial = async () => {
    setBusy(true); setNote(null);
    try {
      await api.startEvalPhoneRecording(Number(speakerId), Number(scriptId), orgId);
      setNote({ text: 'Dialling — answer the phone and read the script.', tone: 'ok' });
      await reload();
    } catch (e) {
      setNote({ text: `Couldn't start the call: ${reason(e)}`, tone: 'error' });
    } finally { setBusy(false); }
  };

  const startBrowser = async () => {
    setNote(null);
    try {
      recorderRef.current = await startEvalRecorder();
      setElapsed(0);
      setRecState('recording');
    } catch (e) {
      setNote({ text: `Couldn't access the microphone: ${reason(e)}`, tone: 'error' });
    }
  };

  const stopBrowser = async () => {
    if (!recorderRef.current) return;
    setRecState('uploading');
    try {
      const wav = await recorderRef.current.stop();
      await api.uploadEvalBrowserRecording(wav, Number(speakerId), Number(scriptId));
      setNote({ text: 'Uploaded — processing.', tone: 'ok' });
      await reload();
    } catch (e) {
      setNote({ text: `Couldn't upload the recording: ${reason(e)}`, tone: 'error' });
    }
    recorderRef.current = null;
    setRecState('idle');
  };

  const remove = async (r: EvalRecording) => {
    if (!window.confirm(`Delete recording #${r.id} and its clips? This can't be undone.`)) return;
    setListNote('');
    try {
      await api.deleteEvalRecording(r.id);
      await reload();
    } catch (e) {
      setListNote(`Couldn't delete recording #${r.id}: ${reason(e)}`);
    }
  };

  const formReady = speakerId && scriptId;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">New recording</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select className="border rounded px-2 py-1.5 text-sm" aria-label="Speaker" value={speakerId} onChange={e => setSpeakerId(e.target.value)}>
            <option value="">Speaker…</option>
            {speakers.map(s => <option key={s.id} value={s.id}>{s.name}{s.phone_e164 ? ` (${s.phone_e164})` : ''}</option>)}
          </select>
          <select className="border rounded px-2 py-1.5 text-sm" aria-label="Script" value={scriptId} onChange={e => setScriptId(e.target.value)}>
            <option value="">Script…</option>
            {scripts.map(s => (
              <option key={s.id} value={s.id}>
                {languageLabel(s.language)} — {s.name}
              </option>
            ))}
          </select>
          <select className="border rounded px-2 py-1.5 text-sm" aria-label="Organization to dial through" value={orgId} onChange={e => setOrgId(e.target.value)}>
            <option value="">Dial via organization…</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={dial}
            disabled={!formReady || !orgId || busy}
            title={!orgId ? 'Pick the organization whose phone number places the call' : undefined}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50"
          >
            <Phone className="w-4 h-4" /> Call the speaker
          </button>
          {recState === 'idle' && (
            <button
              onClick={startBrowser}
              disabled={!formReady}
              className="flex items-center gap-1.5 bg-gray-800 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50"
            >
              <Mic className="w-4 h-4" /> Record in browser
            </button>
          )}
          {recState === 'recording' && (
            <button onClick={stopBrowser} className="flex items-center gap-1.5 bg-red-600 text-white px-4 py-1.5 rounded text-sm">
              <Square className="w-4 h-4" /> Stop &amp; upload ({elapsed}s)
            </button>
          )}
          {recState === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          <NoteLine note={note} />
        </div>
        {script && recState !== 'idle' && (
          <div className="border-t pt-3">
            <p className="text-xs text-gray-500 mb-2">Read one line at a time, short pause between lines:</p>
            <ol className="text-sm space-y-1 list-decimal list-inside max-h-64 overflow-y-auto">
              {script.items.map(i => <li key={i.id} className="text-gray-800">{i.text}</li>)}
            </ol>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {listNote && <p className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">{listNote}</p>}
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">#</th><th className="px-4 py-2">Speaker</th>
              <th className="px-4 py-2">Language</th><th className="px-4 py-2">Source</th>
              <th className="px-4 py-2">Status</th><th className="px-4 py-2">Clips</th>
              <th className="px-4 py-2">Duration</th><th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {recordings.map(r => (
              <React.Fragment key={r.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                  <td className="px-4 py-2 text-gray-500">{r.id}</td>
                  <td className="px-4 py-2">{r.speaker_name || '—'}</td>
                  <td className="px-4 py-2">{languageLabel(r.language)}</td>
                  <td className="px-4 py-2 capitalize">{r.source}</td>
                  <td className="px-4 py-2">
                    <StatusPill status={r.status} />
                    {r.error && <span className="ml-2 text-xs text-red-600" title={r.error}>Failed: {r.error}</span>}
                  </td>
                  <td className="px-4 py-2">{r.clip_count ?? 0}</td>
                  <td className="px-4 py-2">{r.duration_ms ? `${Math.round(r.duration_ms / 1000)}s` : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={e => { e.stopPropagation(); void remove(r); }}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete recording"
                      aria-label={`Delete recording ${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {openId === r.id && (
                  <tr><td colSpan={8} className="bg-gray-50 px-4 py-3">
                    <ClipsPanel api={api} recording={r} reload={reload} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {!loaded && recordings.length === 0 && <LoadingRow colSpan={8} what="recordings" />}
            {loaded && recordings.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  No recordings yet. Pick a speaker and a script above, then call them or record in the browser.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ClipsPanel: React.FC<{ api: AdminAPI; recording: EvalRecording; reload: () => Promise<void> }> =
  ({ api, recording, reload }) => {
    const [clips, setClips] = useState<EvalClip[] | null>(null);
    const [loadError, setLoadError] = useState('');
    const [note, setNote] = useState<Note>(null);
    const [playingId, setPlayingId] = useState<number | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const load = useCallback(async () => {
      setLoadError('');
      try {
        setClips(await api.getEvalClips(recording.id));
      } catch (e) {
        setClips([]);
        setLoadError(`Couldn't load the clips: ${reason(e)}`);
      }
    }, [api, recording.id]);
    useEffect(() => { load(); }, [load]);

    const play = async (clip: EvalClip) => {
      setNote(null);
      try {
        const blob = await api.fetchEvalAudio(`/super-admin/voice-evals/clips/${clip.id}/audio`);
        if (audioRef.current) { audioRef.current.pause(); URL.revokeObjectURL(audioRef.current.src); }
        const audio = new Audio(URL.createObjectURL(blob));
        audioRef.current = audio;
        setPlayingId(clip.id);
        audio.onended = () => setPlayingId(null);
        await audio.play();
      } catch (e) {
        setPlayingId(null);
        setNote({ text: `Couldn't play this clip. ${reason(e)}`, tone: 'error' });
      }
    };

    const reprocess = async () => {
      setNote(null);
      try {
        await api.reprocessEvalRecording(recording.id);
        await reload();
      } catch (e) {
        setNote({ text: `Couldn't re-run segmentation: ${reason(e)}`, tone: 'error' });
      }
    };

    const setIncluded = async (clip: EvalClip, included: boolean) => {
      setNote(null);
      try {
        await api.patchEvalClip(clip.id, { included });
        await load();
      } catch (e) {
        setNote({ text: `Couldn't update clip ${clip.id}: ${reason(e)}`, tone: 'error' });
      }
    };

    if (!clips) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
    if (loadError) {
      return (
        <div className="flex items-center gap-3 text-xs text-red-600">
          <span>{loadError}</span>
          <button onClick={load} className="px-2 py-0.5 rounded border border-red-300 bg-white hover:bg-red-50">Retry</button>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">{clips.length} clips</p>
          <button
            onClick={reprocess}
            title="Cut this recording into clips again from the raw audio"
            className="text-xs text-blue-600 hover:underline"
          >
            Re-run segmentation
          </button>
        </div>
        <NoteLine note={note} />
        {clips.length === 0 && (
          <p className="text-xs text-gray-400">No clips yet. They appear once processing finishes; use Re-run segmentation if it stays empty.</p>
        )}
        <table className="w-full text-xs">
          <thead className="text-left text-gray-500">
            <tr>
              <th className="py-1 pr-3" /><th className="py-1 pr-3">Category</th>
              <th className="py-1 pr-3">Ground truth</th><th className="py-1 pr-3">Transcript used for alignment</th>
              <th className="py-1 pr-3">Match</th><th className="py-1 pr-3">Included</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {clips.map(c => (
              <tr key={c.id} className={c.included ? '' : 'opacity-50'}>
                <td className="py-1 pr-3">
                  <button
                    onClick={() => play(c)}
                    className="text-gray-500 hover:text-blue-600"
                    title="Play clip"
                    aria-label={`Play clip ${c.id}`}
                  >
                    {playingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                </td>
                <td className="py-1 pr-3">{CATEGORY_LABEL[c.category]}</td>
                <td className="py-1 pr-3 max-w-xs truncate" title={c.ground_truth}>{c.ground_truth || <em className="text-gray-400">(silence)</em>}</td>
                <td className="py-1 pr-3 max-w-xs truncate text-gray-500" title={c.align_transcript || ''}>{c.align_transcript || '—'}</td>
                <td className="py-1 pr-3">{c.align_sim !== null ? pct(c.align_sim) : '—'}</td>
                <td className="py-1 pr-3">
                  <input
                    type="checkbox"
                    checked={c.included}
                    aria-label={`Include clip ${c.id} in evaluations`}
                    onChange={e => { void setIncluded(c, e.target.checked); }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

// ── Scripts ──────────────────────────────────────────────────────────────────

const ScriptsTab: React.FC<{ scripts: EvalScript[]; loaded: boolean }> = ({ scripts, loaded }) => {
  if (scripts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-400">
        {loaded
          ? 'No scripts yet. Reading scripts are seeded on the server, not created from this page.'
          : <><Loader2 className="inline w-4 h-4 animate-spin mr-2 align-text-bottom" />Loading scripts…</>}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {scripts.map(s => (
        <div key={s.id} className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-1">
            {languageLabel(s.language)} — {s.name}
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            The reading sheet: one line at a time, short pause between lines. Silence probes are
            harvested automatically from the gaps.
          </p>
          {(['sentence', 'short_phrase', 'one_word'] as const).map(cat => (
            <div key={cat} className="mb-3">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">{CATEGORY_LABEL[cat]}s</p>
              <ol className="text-sm space-y-0.5">
                {s.items.filter(i => i.category === cat).map(i => (
                  <li key={i.id} className="text-gray-800">{i.idx}. {i.text}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// ── Speakers ─────────────────────────────────────────────────────────────────

const SpeakersTab: React.FC<{ api: AdminAPI; speakers: EvalSpeaker[]; loaded: boolean; reload: () => Promise<void> }> =
  ({ api, speakers, loaded, reload }) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [langs, setLangs] = useState<string[]>([]);
    const [note, setNote] = useState<Note>(null);

    const add = async () => {
      setNote(null);
      try {
        await api.createEvalSpeaker({
          name, phone_e164: phone || undefined, languages: langs,
        });
        setName(''); setPhone(''); setLangs([]);
        await reload();
      } catch (e) {
        setNote({ text: `Couldn't add the speaker: ${reason(e)}`, tone: 'error' });
      }
    };

    const remove = async (s: EvalSpeaker) => {
      if (!window.confirm(`Delete speaker "${s.name}"? This can't be undone.`)) return;
      setNote(null);
      try {
        await api.deleteEvalSpeaker(s.id);
        await reload();
      } catch (e) {
        setNote({ text: `Couldn't delete ${s.name}: ${reason(e)}`, tone: 'error' });
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-3">
          <h3 className="font-semibold text-gray-900">Add speaker</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Name
              <input className="border rounded px-2 py-1.5 text-sm text-gray-900" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Phone number (optional)
              <input className="border rounded px-2 py-1.5 text-sm text-gray-900" placeholder="+91XXXXXXXXXX" value={phone} onChange={e => setPhone(e.target.value)} />
            </label>
            <div className="flex flex-col gap-1 text-xs text-gray-500">
              Languages
              <div className="flex gap-2 py-1.5">
                {LANGUAGES.map(l => (
                  <label key={l.value} className="flex items-center gap-1 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={langs.includes(l.value)}
                      onChange={e => setLangs(e.target.checked ? [...langs, l.value] : langs.filter(x => x !== l.value))}
                    />
                    {l.label}
                  </label>
                ))}
              </div>
            </div>
            <button onClick={add} disabled={!name} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          <NoteLine note={note} />
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Phone</th><th className="px-4 py-2">Languages</th><th className="px-4 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {speakers.map(s => (
                <tr key={s.id}>
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2">{s.phone_e164 || '—'}</td>
                  <td className="px-4 py-2">{s.languages.map(languageLabel).join(', ') || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => void remove(s)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete speaker"
                      aria-label={`Delete speaker ${s.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loaded && speakers.length === 0 && <LoadingRow colSpan={4} what="speakers" />}
              {loaded && speakers.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No speakers yet. Add one above to start recording.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

// ── Runs ─────────────────────────────────────────────────────────────────────

const RunsTab: React.FC<{ api: AdminAPI; runs: EvalRun[]; loaded: boolean; reload: () => Promise<void> }> =
  ({ api, runs, loaded, reload }) => {
    const [providers, setProviders] = useState<string[]>(['groq']);
    const [language, setLanguage] = useState('');
    const [source, setSource] = useState('');
    const [name, setName] = useState('');
    const [note, setNote] = useState<Note>(null);
    const [openId, setOpenId] = useState<number | null>(null);

    const create = async () => {
      setNote(null);
      try {
        await api.createEvalRun({
          name: name || undefined, providers,
          language: language || undefined, source: source || undefined,
        });
        setName('');
        await reload();
      } catch (e) {
        setNote({ text: `Couldn't start the evaluation: ${reason(e)}`, tone: 'error' });
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-3">
          <h3 className="font-semibold text-gray-900">New run</h3>
          <div className="flex flex-wrap gap-3 items-center">
            <input className="border rounded px-2 py-1.5 text-sm" placeholder="Name (optional)" aria-label="Run name (optional)" value={name} onChange={e => setName(e.target.value)} />
            {['groq'].map(p => (
              <label key={p} className="flex items-center gap-1 text-sm text-gray-700 capitalize">
                <input
                  type="checkbox"
                  checked={providers.includes(p)}
                  onChange={e => setProviders(e.target.checked ? [...providers, p] : providers.filter(x => x !== p))}
                />
                {p}
              </label>
            ))}
            <select className="border rounded px-2 py-1.5 text-sm" aria-label="Language filter" value={language} onChange={e => setLanguage(e.target.value)}>
              <option value="">All languages</option>
              {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <select className="border rounded px-2 py-1.5 text-sm" aria-label="Source filter" value={source} onChange={e => setSource(e.target.value)}>
              <option value="">Phone + browser</option>
              <option value="phone">Phone only</option>
              <option value="browser">Browser only</option>
            </select>
            <button onClick={create} disabled={providers.length === 0} className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">
              Run evaluation
            </button>
            <NoteLine note={note} />
          </div>
          <p className="text-xs text-gray-500">
            Transcribes every included clip that matches these filters with the selected providers, then scores each transcript against its ground truth.
          </p>
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">#</th><th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Providers</th><th className="px-4 py-2">Filters</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Results</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map(r => (
                <React.Fragment key={r.id}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                    <td className="px-4 py-2 text-gray-500">{r.id}</td>
                    <td className="px-4 py-2">{r.name || '—'}</td>
                    <td className="px-4 py-2">{r.providers.map(capitalise).join(', ')}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{describeFilters(r.filters)}</td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.status} />
                      {r.error && <span className="ml-2 text-xs text-red-600" title={r.error}>Failed: {r.error}</span>}
                    </td>
                    <td className="px-4 py-2">{r.result_count ?? 0}</td>
                  </tr>
                  {openId === r.id && (
                    <tr><td colSpan={6} className="bg-gray-50 px-4 py-3"><RunDetailsPanel api={api} runId={r.id} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {!loaded && runs.length === 0 && <LoadingRow colSpan={6} what="runs" />}
              {loaded && runs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No runs yet. Start one above once you have recordings with clips.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

const RunDetailsPanel: React.FC<{ api: AdminAPI; runId: number }> = ({ api, runId }) => {
  const [details, setDetails] = useState<EvalRunDetails | null>(null);
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    setLoadError('');
    api.getEvalRunDetails(runId)
      .then(setDetails)
      .catch((e: unknown) => { setDetails(null); setLoadError(`Couldn't load this run's results: ${reason(e)}`); });
  }, [api, runId]);

  if (loadError) return <p className="text-xs text-red-600">{loadError}</p>;
  if (!details) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;

  // Pivot: provider rows, category columns (avg WER), plus overall.
  const providers = details.run.providers;
  const categories = Array.from(new Set(details.aggregates.map(a => a.category)));
  const cell = (provider: string, category?: string) => {
    const rows = details.aggregates.filter(a => a.provider === provider && (!category || a.category === category));
    const clips = rows.reduce((n, r) => n + Number(r.clips), 0);
    if (!clips) return { wer: null as number | null, clips: 0, latency: null as number | null };
    const wer = rows.reduce((n, r) => n + (Number(r.avg_wer ?? 0) * Number(r.clips)), 0) / clips;
    const latency = rows.reduce((n, r) => n + (Number(r.avg_latency_ms ?? 0) * Number(r.clips)), 0) / clips;
    return { wer, clips, latency };
  };
  const werTitle = 'Word error rate — lower is better';

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        WER (word error rate) — lower is better. Clip counts are in brackets.
      </p>
      <table className="text-xs border-collapse">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1 pr-4">Provider</th>
            {categories.map(c => <th key={c} className="py-1 pr-4" title={werTitle}>{CATEGORY_LABEL[c] || c} WER</th>)}
            <th className="py-1 pr-4" title={werTitle}>Overall WER</th>
            <th className="py-1 pr-4">Avg latency</th>
          </tr>
        </thead>
        <tbody>
          {providers.map(p => {
            const overall = cell(p);
            return (
              <tr key={p} className="border-t border-gray-200">
                <td className="py-1.5 pr-4 font-medium capitalize">{p}</td>
                {categories.map(c => {
                  const v = cell(p, c);
                  return <td key={c} className="py-1.5 pr-4">{pct(v.wer)} <span className="text-gray-400">({v.clips})</span></td>;
                })}
                <td className="py-1.5 pr-4 font-semibold">{pct(overall.wer)}</td>
                <td className="py-1.5 pr-4">{overall.latency !== null ? `${Math.round(overall.latency)}ms` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <details>
        <summary className="text-xs text-blue-600 cursor-pointer">Per-clip transcripts</summary>
        <table className="w-full text-xs mt-2">
          <thead className="text-left text-gray-500">
            <tr>
              <th className="py-1 pr-3">Clip</th><th className="py-1 pr-3">Category</th>
              <th className="py-1 pr-3">Ground truth</th><th className="py-1 pr-3">Provider</th>
              <th className="py-1 pr-3">Transcript</th><th className="py-1 pr-3" title={werTitle}>WER</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {details.results.map(x => (
              <tr key={x.id} className={x.wer !== null && x.wer > 0.3 ? 'bg-red-50' : ''}>
                <td className="py-1 pr-3 text-gray-400">{x.clip_id}</td>
                <td className="py-1 pr-3">{CATEGORY_LABEL[x.category]}</td>
                <td className="py-1 pr-3 max-w-[16rem] truncate" title={x.ground_truth}>{x.ground_truth || <em>(silence)</em>}</td>
                <td className="py-1 pr-3 capitalize">{x.provider}</td>
                <td className="py-1 pr-3 max-w-[16rem] truncate" title={x.transcript || ''}>{x.error ? <span className="text-red-600">{x.error}</span> : (x.transcript || <em>(empty)</em>)}</td>
                <td className="py-1 pr-3">{pct(x.wer)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};
